import { Box, Button, styled } from "@mui/material";
import * as CSC from "SC";
import * as SCCL from "components/CommonLayout.style";
import { MessageIndicator } from "components/MessageIndicator";
import { useMessageIndicator } from "hooks/useMessageIndicator";
import BurgerIcon from "images/burger-menu-icon.svg";
import { ReactComponent as Logo } from "images/rs-logo.svg";
import { customColors } from "models/customColors";
import React, { useContext } from "react";
import { GlobalContext } from "store/globalContext";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { toggleMenu } from "store/reducers/isMenuVisibleReducer";
import { BackToPrevPage } from "./BackToPrevPage";
import * as SC from "./CommonOutletTemplate.style";
import { HelpIcon } from "./HelpIcon";
import { ModalSpinner } from "components/ModalSpinner";
import { useNavigate } from "react-router-dom";

const Avatar = styled(SCCL.Avatar)`
  width: 40px;
  height: 40px;
  font-size: 16px;
`;

const UserName = styled(SCCL.UserName)`
  font-size: 14px;
`;

/** @param {{ title?: React.ReactNode; isLoading?: boolean; backIcon?: boolean; backLink?: import("react-router-dom").To | number; refreshData?: ()=> void; comment?: string|JSX.Element; noPadding?: boolean;  } & React.PropsWithChildren} props */
export const CommonOutletTemplate = ({
  title = "",
  isLoading = false,
  children,
  backIcon = false,
  backLink = -1,
  refreshData = undefined,
  comment = "",
  noPadding = false,
}) => {
  const dispatch = useAppDispatch();
  const {
    // @ts-ignore
    userData: { fullName, role, email, organization },
  } = useContext(GlobalContext);
  const spinner = useAppSelector((state) => state.spinner);
  const navigate = useNavigate();

  useMessageIndicator({ role });
  const paymentData = organization?.paymentData;
  const isSuperAdmin = role === "Super Admin";
  const isPaymentDetailsNeeded =
    !isSuperAdmin && role !== "Scheduler" && !paymentData?.payment_method_id;

  const nameAbbreviation = (fullName ?? role ?? " ")
    .split(" ")
    .map((name) => (name?.length ? name[0] : "").toUpperCase())
    .join("")
    .slice(0, 2);

  const isPaymentMethodPage =
    window.location.pathname.includes("payment_method");

  return (
    <SC.Container>
      <div className="flex flex-row justify-between items-center gap-0.5 w-100 pl-4 bg-gray-200 lg:bg-white">
        <div className="block lg:hidden hover:opacity-80 cursor-pointer">
          <img
            width="42px"
            src={BurgerIcon}
            alt="Menu Icon"
            onClick={() => dispatch(toggleMenu())}
          />
        </div>
        <div className="hidden lg:flex flex-row justify-start items-center gap-2">
          {backIcon && <BackToPrevPage backLink={backLink} />}
          <CSC.Header3>{title}</CSC.Header3>
          <div className="text-sm text-gray-400">
            <HelpIcon>{comment}</HelpIcon>
          </div>
        </div>
        <div className="flex lg:hidden flex-row justify-start items-center gap-2">
          {!!nameAbbreviation && <Avatar>{nameAbbreviation}</Avatar>}
          <div className="flex flex-col items-start gap-0">
            {!!fullName && <UserName>{fullName}</UserName>}
            <SCCL.Recipient>
              {organization?.name ? `${role}, ${organization.name}` : role}
            </SCCL.Recipient>
            <SCCL.Email className="hidden sm:block">{email}</SCCL.Email>
          </div>
        </div>
        <div className="flex flex-row items-center">
          {["Admin", "Marketer", "Scheduler"].includes(role) && (
            <MessageIndicator />
          )}
          <Logo width={83} height={35} style={{ margin: "14px 20px" }} />
        </div>
      </div>
      {isPaymentDetailsNeeded && (
        <div
          className={[
            "flex flex-row justify-between items-center",
            "bg-red-100 text-red-800 p-2 px-4 mx-6 md:mx-8 mt-2 rounded-lg text-sm",
          ].join(" ")}
        >
          Please update your payment method to continue using Referral Stream
          {!isPaymentMethodPage && (
            <Button
              variant="contained"
              color="error"
              size="small"
              sx={{ padding: "8px 16px", boxShadow: "none" }}
              onClick={() => navigate("/payment_method")}
            >
              Update Payment Method
            </Button>
          )}
        </div>
      )}
      <div
        className={`
          flex-1 flex flex-col justify-between items-stretch 
          ${noPadding ? "p0" : "p-5 pt-3 md:pr-8 md:pb-8 md:pl-8"} 
          h-full overflow-auto
        `}
      >
        <div
          className={`
            flex flex-row items-center gap-4
            ${noPadding ? "pl-4 pt-2" : "p0"}
            lg:hidden
          `}
        >
          {backIcon && <BackToPrevPage backLink={backLink} isMobile />}
          <SC.Title>{title}</SC.Title>
          <div className="text-sm text-gray-400">
            <HelpIcon>{comment}</HelpIcon>
          </div>
        </div>
        {isLoading && (
          <Box sx={{ color: customColors.grayText }}>Loading...</Box>
        )}
        {!isLoading && children}
      </div>
      <ModalSpinner open={spinner} />
    </SC.Container>
  );
};
